import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { isAxiosError } from 'axios';
import { API_ENDPOINTS } from '@/config/config';
import { useInvalidateAttributeGroupsCache } from '@/hooks/useGlobalAttributes';
import { logError, logWarn } from '@/utils/errorHandler';

// Define types
export interface AttributeGroup {
  id: number;
  name: string;
  description?: string;
  organization: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  order?: number;
  version?: number;
  updated_by?: number;
  usage?: AttributeGroupUsage;
}

export interface AttributeGroupUsage {
  productCount: number;
  attributeCount: number;
  hasValues: boolean;
  familyCount: number;
}

// Query keys
export const attributeGroupKeys = {
  all: ['attributeGroups'] as const,
  lists: () => [...attributeGroupKeys.all, 'list'] as const,
  list: (filters: any) => [...attributeGroupKeys.lists(), { filters }] as const,
  details: () => [...attributeGroupKeys.all, 'detail'] as const,
  detail: (id: number) => [...attributeGroupKeys.details(), id] as const,
};

// Error handling helper
const handleApiError = (error: unknown) => {
  if (isAxiosError(error)) {
    const axiosError = error;
    if (axiosError.response?.status === 403) {
      throw new Error('You do not have permission to perform this action');
    }
    if (axiosError.response?.data) {
      if (axiosError.response.data.detail) {
        throw new Error(axiosError.response.data.detail);
      }
      throw new Error(JSON.stringify(axiosError.response.data));
    }
  }
  throw error;
};

// API functions
export const getAttributeGroups = async (): Promise<AttributeGroup[]> => {
  try {
    const { data } = await axiosInstance.get(API_ENDPOINTS.attributeGroups.list);
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getAttributeGroup = async (id: number): Promise<AttributeGroup> => {
  try {
    const { data } = await axiosInstance.get(API_ENDPOINTS.attributeGroups.update(id));
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const createAttributeGroup = async (data: { name: string; order?: number }) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.attributeGroups.create, {
      name: data.name,
      ...(data.order !== undefined && { order: data.order }),
    });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateAttributeGroup = async (
  id: number,
  data: { name?: string; order?: number; version?: number }
) => {
  try {
    const response = await axiosInstance.patch(API_ENDPOINTS.attributeGroups.update(id), data);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteAttributeGroup = async (id: number) => {
  try {
    await axiosInstance.delete(API_ENDPOINTS.attributeGroups.delete(id));
  } catch (error) {
    return handleApiError(error);
  }
};

export const reorderAttributeGroupItems = async (groupId: number, itemIds: number[]) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.attributeGroups.reorderItems(groupId), {
      item_ids: itemIds,
    });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const addAttributeToGroup = async (groupId: number, attributeId: number) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.attributeGroups.addItem(groupId), {
      attribute: attributeId,
    });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const removeAttributeFromGroup = async (groupId: number, itemId: number) => {
  try {
    const response = await axiosInstance.delete(
      API_ENDPOINTS.attributeGroups.removeItem(groupId, itemId)
    );
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const reorderAttributeGroups = async (groupIds: number[]) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.attributeGroups.reorder, {
      group_ids: groupIds,
    });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// React Query hooks
export const useAttributeGroups = () => {
  return useQuery({
    queryKey: attributeGroupKeys.lists(),
    queryFn: getAttributeGroups,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('permission')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useAttributeGroup = (id: number) => {
  return useQuery({
    queryKey: attributeGroupKeys.detail(id),
    queryFn: () => getAttributeGroup(id),
    enabled: !!id,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('permission')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useCreateAttributeGroup = () => {
  const queryClient = useQueryClient();
  const invalidateAttributeGroupsCache = useInvalidateAttributeGroupsCache();

  return useMutation({
    mutationFn: createAttributeGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.lists() });
      invalidateAttributeGroupsCache();
    },
    onError: (error: Error) => {
      logError(
        'Failed to create attribute group',
        {
          component: 'attributeGroupApi',
          action: 'useCreateAttributeGroup',
          metadata: { errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useUpdateAttributeGroup = (id?: number) => {
  const queryClient = useQueryClient();
  const invalidateAttributeGroupsCache = useInvalidateAttributeGroupsCache();

  return useMutation({
    mutationFn: (data: { name?: string; order?: number; version?: number }) => {
      if (!id) throw new Error('Attribute group ID is required');
      return updateAttributeGroup(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.lists() });
      if (id) {
        queryClient.invalidateQueries({ queryKey: attributeGroupKeys.detail(id) });
      }
      invalidateAttributeGroupsCache();
    },
    onError: (error: Error) => {
      logError(
        'Failed to update attribute group',
        {
          component: 'attributeGroupApi',
          action: 'useUpdateAttributeGroup',
          metadata: { attributeGroupId: id, errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useDeleteAttributeGroup = () => {
  const queryClient = useQueryClient();
  const invalidateAttributeGroupsCache = useInvalidateAttributeGroupsCache();

  return useMutation({
    mutationFn: deleteAttributeGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.lists() });
      invalidateAttributeGroupsCache();
    },
    onError: (error: Error) => {
      logError(
        'Failed to delete attribute group',
        {
          component: 'attributeGroupApi',
          action: 'useDeleteAttributeGroup',
          metadata: { errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useReorderAttributeGroupItems = (groupId: number) => {
  const queryClient = useQueryClient();
  const invalidateAttributeGroupsCache = useInvalidateAttributeGroupsCache();

  return useMutation({
    mutationFn: (itemIds: number[]) => reorderAttributeGroupItems(groupId, itemIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.lists() });
      invalidateAttributeGroupsCache();
    },
    onError: (error: Error) => {
      logError(
        'Failed to reorder attribute group items',
        {
          component: 'attributeGroupApi',
          action: 'useReorderAttributeGroupItems',
          metadata: { groupId, errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useAddAttributeToGroup = (groupId: number) => {
  const queryClient = useQueryClient();
  const invalidateAttributeGroupsCache = useInvalidateAttributeGroupsCache();

  return useMutation({
    mutationFn: (attributeId: number) => addAttributeToGroup(groupId, attributeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.lists() });
      invalidateAttributeGroupsCache();
    },
    onError: (error: Error) => {
      logError(
        'Failed to add attribute to group',
        {
          component: 'attributeGroupApi',
          action: 'useAddAttributeToGroup',
          metadata: { groupId, errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useRemoveAttributeFromGroup = (groupId: number) => {
  const queryClient = useQueryClient();
  const invalidateAttributeGroupsCache = useInvalidateAttributeGroupsCache();

  return useMutation({
    mutationFn: (itemId: number) => removeAttributeFromGroup(groupId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.lists() });
      invalidateAttributeGroupsCache();
    },
    onError: (error: Error) => {
      logError(
        'Failed to remove attribute from group',
        {
          component: 'attributeGroupApi',
          action: 'useRemoveAttributeFromGroup',
          metadata: { groupId, errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useReorderAttributeGroups = () => {
  const queryClient = useQueryClient();
  const invalidateAttributeGroupsCache = useInvalidateAttributeGroupsCache();

  return useMutation({
    mutationFn: reorderAttributeGroups,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.lists() });
      invalidateAttributeGroupsCache();
    },
    onError: (error: Error) => {
      logError(
        'Failed to reorder attribute groups',
        {
          component: 'attributeGroupApi',
          action: 'useReorderAttributeGroups',
          metadata: { errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

// Enhanced attribute groups with usage information from effective attributes
export const getAttributeGroupsWithUsage = async (): Promise<AttributeGroup[]> => {
  try {
    // Try the optimized endpoint first
    const { data } = await axiosInstance.get('/api/attribute-groups/with-usage/');
    return data;
  } catch (error) {
    logWarn(
      'Optimized endpoint not available, using fallback',
      {
        component: 'attributeGroupApi',
        action: 'getAttributeGroupsWithUsage',
        metadata: { error },
      },
      false
    );

    // Fallback to basic attribute groups with minimal usage data
    try {
      const attributeGroups = await getAttributeGroups();
      return attributeGroups.map(group => ({
        ...group,
        usage: {
          productCount: 0,
          attributeCount: 0,
          hasValues: false,
          familyCount: 0,
        },
      }));
    } catch (fallbackError) {
      logError(
        'Error getting attribute groups with usage',
        {
          component: 'attributeGroupApi',
          action: 'getAttributeGroupsWithUsage',
          metadata: { error: fallbackError },
        },
        { showToast: false, userFacing: false }
      );
      throw fallbackError;
    }
  }
};

// Enhanced hook with usage information from effective attributes
export const useAttributeGroupsWithUsage = () => {
  return useQuery({
    queryKey: [...attributeGroupKeys.lists(), 'withUsage'],
    queryFn: getAttributeGroupsWithUsage,
    staleTime: 2 * 60 * 1000, // 2 minutes - shorter than regular groups since usage can change
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('permission')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};
